import { print, getOperationAST, GraphQLFormattedError } from 'graphql';

import { ZVUK_API_GRAPHQL_URL } from 'application/consts';
import { setTokenCookie } from '@shared/lib';

import DataProviderUtils from 'application/providers/DataProvider/utils';

import { UploadFileThunkArg } from './uploadFileThunkArg';
import { UploadFileResult } from './uploadFileResult';

type MultipartUploadArg = Pick<UploadFileThunkArg, 'document' | 'variables' | 'filePath' | 'file'>;

type XhrUploadResponse = { data?: UploadFileResult; errors?: GraphQLFormattedError[] };

const stripFileFromVariables = (variables: Record<string, unknown>): Record<string, unknown> =>
  JSON.parse(JSON.stringify(variables, (_key, value) => (value instanceof File ? null : value)));

const sendMultipartRequest = (
  arg: MultipartUploadArg,
  onProgress: (progress: number) => void
): Promise<XhrUploadResponse> => {
  const operations = JSON.stringify({
    operationName: getOperationAST(arg.document)?.name?.value,
    query: print(arg.document),
    variables: stripFileFromVariables(arg.variables),
  });

  const formData = new FormData();
  formData.append('operations', operations);
  formData.append('map', JSON.stringify({ 0: [`variables.${arg.filePath}`] }));
  formData.append('0', arg.file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('POST', ZVUK_API_GRAPHQL_URL);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      try {
        resolve(JSON.parse(xhr.responseText));
      } catch {
        reject(new Error('Invalid upload response'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));

    xhr.send(formData);
  });
};

export const uploadFileWithProgress = async (
  arg: MultipartUploadArg,
  onProgress: (progress: number) => void
): Promise<UploadFileResult> => {
  const response = await sendMultipartRequest(arg, onProgress);

  if (!response.errors?.length) {
    return response.data || {};
  }

  if (!DataProviderUtils.checkIsJWTExpiredError(response.errors)) {
    throw new Error(response.errors[0].message);
  }

  const refreshedToken = await DataProviderUtils.tokenRefresh();

  if (!refreshedToken) {
    throw new Error(response.errors[0].message);
  }

  setTokenCookie(refreshedToken);

  const retryResponse = await sendMultipartRequest(arg, onProgress);

  if (retryResponse.errors?.length) {
    throw new Error(retryResponse.errors[0].message);
  }

  return retryResponse.data || {};
};
